/**
 * Adding a camera to a device.
 *
 * Two RTSP URIs, because the camera model holds two profiles per camera from the
 * outset. The substream is required — it is what is recorded continuously — and
 * the mainstream is optional, because it is event-only and a single-profile
 * camera must still be usable.
 *
 * The credential never leaves this component as plaintext. It is encrypted here,
 * in the browser, to the public key of the device that will use it; what goes
 * over the wire and into the database is ciphertext the server cannot read. That
 * is the whole design — a breach of the cloud must not hand someone the RTSP
 * credentials and camera addresses for every installation.
 */

import { useState, type FormEvent } from 'react';

import { sealCameraCredential } from '@jeprotect/shared/camera-credential';

import { addCamera, ApiError, type SealedCredentials } from './api.ts';
import { validateCamera } from './forms.ts';
import { useDeviceKey } from './useDeviceKey.ts';

export function AddCamera({
  deviceId,
  onAdded,
}: {
  deviceId: string;
  onAdded: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [onvifHost, setOnvifHost] = useState('');
  const [subProfileUri, setSubProfileUri] = useState('');
  const [mainProfileUri, setMainProfileUri] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * The key is fetched when the form opens, not when the page loads, so the
   * window between reading it and sealing to it is as short as possible.
   */
  const key = useDeviceKey(deviceId, open);

  function reset(): void {
    setName('');
    setOnvifHost('');
    setSubProfileUri('');
    setMainProfileUri('');
    setUsername('');
    setPassword('');
  }

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setMessage(null);

    const check = validateCamera({ onvifHost, subProfileUri, mainProfileUri, username, password });
    if (!check.ok) {
      setErrors(check.errors);
      return;
    }
    setErrors({});

    if (username.trim() !== '' && key.status !== 'ready') {
      setMessage('This device has not published an encryption key yet, so credentials cannot be saved.');
      return;
    }

    setBusy(true);

    try {
      let credentials: SealedCredentials | undefined;

      if (username.trim() !== '' && key.status === 'ready') {
        /*
         * Sealed here. The plaintext exists in this function and in the two input
         * elements, and nowhere else — not in a request body, not in a log, not
         * in the database.
         *
         * The fingerprint travels with it so the server can refuse a credential
         * sealed to a key the device no longer holds, rather than storing
         * something that silently never decrypts.
         */
        credentials = {
          sealed: await sealCameraCredential(key.publicKey, { username: username.trim(), password }),
          keyFingerprint: key.fingerprint,
        };
      }

      await addCamera({
        deviceId,
        name: name.trim() === '' ? undefined : name.trim(),
        onvifHost: onvifHost.trim(),
        subProfileUri: subProfileUri.trim(),
        mainProfileUri: mainProfileUri.trim() === '' ? undefined : mainProfileUri.trim(),
        credentials,
      });

      reset();
      setOpen(false);
      onAdded();
    } catch (error) {
      /*
       * The refusals get their own wording. "Licensed for 4 cameras", "the
       * subscription has lapsed" and "the device key changed" are different
       * problems with different fixes, and a generic failure would send someone
       * checking the wrong one.
       */
      if (error instanceof ApiError && error.code === 'entitlement.expired') {
        setMessage(
          'This device’s entitlement has lapsed. Existing cameras keep recording, but no more can be added.',
        );
      } else if (error instanceof ApiError && error.code === 'credentials.key_mismatch') {
        setMessage(
          'This device’s encryption key changed while this form was open. Reload the page and enter the credentials again.',
        );
      } else if (error instanceof ApiError && error.code === 'device.no_public_key') {
        setMessage('This device has not finished claiming itself, so credentials cannot be saved yet.');
      } else {
        setMessage(error instanceof ApiError ? error.message : 'Could not add the camera.');
      }
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" className="btn btn--sm" onClick={() => setOpen(true)}>
        Add camera
      </button>
    );
  }

  return (
    <form className="form" onSubmit={(e) => void submit(e)}>
      <div className="field"><label htmlFor={`name-${deviceId}`}>Camera name (optional)</label>
      <input
        id={`name-${deviceId}`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Front door"
      /></div>

      <div className="field"><label htmlFor={`host-${deviceId}`}>Camera address</label>
      <input
        id={`host-${deviceId}`}
        value={onvifHost}
        onChange={(e) => setOnvifHost(e.target.value)}
        placeholder="10.0.0.5"
      /></div>
      {errors['onvifHost'] ? <span className="field__error">{errors['onvifHost']}</span> : null}

      <div className="field"><label htmlFor={`sub-${deviceId}`}>Substream URI (recorded)</label>
      <input
        id={`sub-${deviceId}`}
        value={subProfileUri}
        onChange={(e) => setSubProfileUri(e.target.value)}
        placeholder="rtsp://10.0.0.5:554/Streaming/Channels/102"
      /></div>
      {errors['subProfileUri'] ? (
        <span className="field__error">{errors['subProfileUri']}</span>
      ) : null}

      <div className="field"><label htmlFor={`main-${deviceId}`}>Mainstream URI (optional)</label>
      <input
        id={`main-${deviceId}`}
        value={mainProfileUri}
        onChange={(e) => setMainProfileUri(e.target.value)}
        placeholder="rtsp://10.0.0.5:554/Streaming/Channels/101"
      /></div>
      {errors['mainProfileUri'] ? (
        <span className="field__error">{errors['mainProfileUri']}</span>
      ) : null}

      <div className="field"><label htmlFor={`user-${deviceId}`}>Camera username</label>
      <input
        id={`user-${deviceId}`}
        value={username}
        autoComplete="off"
        onChange={(e) => setUsername(e.target.value)}
        placeholder="admin"
        disabled={key.status !== 'ready'}
      /></div>
      {errors['username'] ? <span className="field__error">{errors['username']}</span> : null}

      <div className="field"><label htmlFor={`pass-${deviceId}`}>Camera password</label>
      <input
        id={`pass-${deviceId}`}
        type="password"
        value={password}
        autoComplete="new-password"
        onChange={(e) => setPassword(e.target.value)}
        disabled={key.status !== 'ready'}
      /></div>
      {errors['password'] ? <span className="field__error">{errors['password']}</span> : null}

      {/*
        * Said plainly, because it changes what the operator should expect: the
        * credential cannot be shown back to them, and replacing the device means
        * entering it again.
        */}
      {key.status === 'ready' ? (
        <p className="form__hint">
          Encrypted in your browser to this device before it is sent. JE Protect cannot read it, and
          it cannot be displayed again — only replaced.
        </p>
      ) : null}
      {key.status === 'loading' ? <p className="form__hint">Loading the device key…</p> : null}
      {key.status === 'absent' ? (
        <p className="notice notice--warn">
          This device has not claimed itself yet, so credentials cannot be saved. The camera can be
          added now and credentials entered once the device is online.
        </p>
      ) : null}
      {key.status === 'error' ? <p className="notice notice--error">{key.message}</p> : null}

      <div className="form__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={busy}>
          {busy ? 'Adding…' : 'Add camera'}
        </button>
        <button type="button" className="btn btn--sm" onClick={() => setOpen(false)}>
          Cancel
        </button>
      </div>

      {message ? (
        <p className="notice notice--error" role="alert">
          {message}
        </p>
      ) : null}
    </form>
  );
}
